/**
 * The navigation icon set.
 *
 * Inline SVG on a 24-unit grid, stroked with `currentColor` so a single CSS rule colours the whole
 * rail and its active state. Drawn here rather than pulled from an icon package: the rail renders on
 * every authenticated page, and a font or sprite would be a second request before the app is usable.
 */

function Icon({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <svg className="rail__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      {children}
    </svg>
  );
}

export function HomeIcon(): React.JSX.Element {
  return (
    <Icon>
      <path d="M4 10.5 12 4l8 6.5" />
      <path d="M6 10v9h12v-9" />
      <path d="M10 19v-5h4v5" />
    </Icon>
  );
}

export function CrmIcon(): React.JSX.Element {
  return (
    <Icon>
      <circle cx="9" cy="8" r="3" />
      <path d="M3.5 19a5.5 5.5 0 0 1 11 0" />
      <path d="M16 7h5" />
      <path d="M16 11h5" />
      <path d="M17 15h4" />
    </Icon>
  );
}

export function PipelineIcon(): React.JSX.Element {
  return (
    <Icon>
      <rect x="3" y="4" width="5" height="16" rx="1.5" />
      <rect x="10" y="4" width="5" height="11" rx="1.5" />
      <rect x="17" y="4" width="4" height="7" rx="1.5" />
    </Icon>
  );
}

export function InboxIcon(): React.JSX.Element {
  return (
    <Icon>
      <path d="M4 13V6.5A1.5 1.5 0 0 1 5.5 5h13A1.5 1.5 0 0 1 20 6.5V13" />
      <path d="M4 13h4l1.5 2.5h5L16 13h4v4.5A1.5 1.5 0 0 1 18.5 19h-13A1.5 1.5 0 0 1 4 17.5Z" />
    </Icon>
  );
}

export function MarketingIcon(): React.JSX.Element {
  return (
    <Icon>
      <path d="M4 10v4a1 1 0 0 0 1 1h3l5 4V5L8 9H5a1 1 0 0 0-1 1Z" />
      <path d="M17 9.5a4 4 0 0 1 0 5" />
      <path d="M19.5 7a7.5 7.5 0 0 1 0 10" />
    </Icon>
  );
}

export function WorkIcon(): React.JSX.Element {
  return (
    <Icon>
      <rect x="3" y="7" width="18" height="13" rx="2" />
      <path d="M9 7V5.5A1.5 1.5 0 0 1 10.5 4h3A1.5 1.5 0 0 1 15 5.5V7" />
      <path d="M3 12h18" />
    </Icon>
  );
}

export function CalendarIcon(): React.JSX.Element {
  return (
    <Icon>
      <rect x="3.5" y="5" width="17" height="15" rx="2" />
      <path d="M3.5 10h17" />
      <path d="M8 3v4" />
      <path d="M16 3v4" />
    </Icon>
  );
}

export function ChatIcon(): React.JSX.Element {
  return (
    <Icon>
      <path d="M20 12.5c0 3.6-3.6 6.5-8 6.5a9.6 9.6 0 0 1-2.6-.35L4.5 20.5l1.1-3.2A6.2 6.2 0 0 1 4 12.5C4 8.9 7.6 6 12 6s8 2.9 8 6.5Z" />
    </Icon>
  );
}

export function AutomationIcon(): React.JSX.Element {
  return (
    <Icon>
      <circle cx="6" cy="6" r="2.5" />
      <circle cx="18" cy="18" r="2.5" />
      <circle cx="6" cy="18" r="2.5" />
      <path d="M8.5 6H14a3.5 3.5 0 0 1 0 7H8.5" />
      <path d="M6 8.5V15.5" />
    </Icon>
  );
}

/** A head in profile with a circuit inside it: the usual shorthand, without borrowing anybody's. */
export function AgentIcon(): React.JSX.Element {
  return (
    <Icon>
      <path d="M15.5 20.5v-2.2a5.5 5.5 0 0 0 3.2-8.6A6.5 6.5 0 0 0 6.4 8.2 4 4 0 0 0 7 16h1.5v4.5" />
      <circle cx="12" cy="10.5" r="1.4" />
      <path d="M12 12v3" />
    </Icon>
  );
}

export function FilesIcon(): React.JSX.Element {
  return (
    <Icon>
      <path d="M4 7.5A1.5 1.5 0 0 1 5.5 6h3.6l1.7 2h7.7A1.5 1.5 0 0 1 20 9.5v8A1.5 1.5 0 0 1 18.5 19h-13A1.5 1.5 0 0 1 4 17.5Z" />
    </Icon>
  );
}

export function ReportingIcon(): React.JSX.Element {
  return (
    <Icon>
      <path d="M4 20V4" />
      <path d="M4 20h16" />
      <rect x="7.5" y="12" width="3" height="5" rx="0.8" />
      <rect x="13" y="8" width="3" height="9" rx="0.8" />
      <rect x="18" y="14" width="2.5" height="3" rx="0.8" />
    </Icon>
  );
}

export function SettingsIcon(): React.JSX.Element {
  return (
    <Icon>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 3v2.2M12 18.8V21M21 12h-2.2M5.2 12H3M18.4 5.6l-1.6 1.6M7.2 16.8l-1.6 1.6M18.4 18.4l-1.6-1.6M7.2 7.2 5.6 5.6" />
    </Icon>
  );
}

/** Customer success: a rising line, which is what the job is measured on. */
export function SuccessIcon(): React.JSX.Element {
  return (
    <Icon>
      <path d="M4 18.5 9 13l3.5 3.5L20 9" />
      <path d="M15 9h5v5" />
    </Icon>
  );
}

export function ShieldIcon(): React.JSX.Element {
  return (
    <Icon>
      <path d="M12 3.5 5 6v6c0 4.2 2.9 7.4 7 8.5 4.1-1.1 7-4.3 7-8.5V6Z" />
      <path d="m9 12 2 2 4-4" />
    </Icon>
  );
}

export function SearchIcon(): React.JSX.Element {
  return (
    <svg className="topbar__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <circle cx="11" cy="11" r="6.5" />
      <path d="m16 16 4 4" />
    </svg>
  );
}

export function BellIcon(): React.JSX.Element {
  return (
    <svg className="topbar__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M6.5 10a5.5 5.5 0 0 1 11 0c0 4 1.5 5.5 1.5 5.5H5S6.5 14 6.5 10Z" />
      <path d="M10 18.5a2.2 2.2 0 0 0 4 0" />
    </svg>
  );
}

/** A pin, for keeping something at the top of a list. */
export function PinIcon(): React.JSX.Element {
  return (
    <Icon>
      <path d="M9 4h6l-1 5 3 3v2h-5v5l-1 1-1-1v-5H5v-2l3-3-1-5Z" />
    </Icon>
  );
}

export function ChevronIcon(): React.JSX.Element {
  return (
    <svg className="rail__chevron" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="m9 6 6 6-6 6" />
    </svg>
  );
}

export function CollapseIcon(): React.JSX.Element {
  return (
    <svg className="rail__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <rect x="3.5" y="4.5" width="17" height="15" rx="2" />
      <path d="M10 4.5v15" />
    </svg>
  );
}

/** Choosing how the rail looks. */
export function PaletteIcon(): React.JSX.Element {
  return (
    <svg className="rail__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M12 3.5a8.5 8.5 0 0 0 0 17c1.4 0 2-.9 2-1.8 0-.9-.7-1.5-.7-2.3 0-.8.6-1.4 1.5-1.4h1.6A4.1 4.1 0 0 0 20.5 11c0-4.1-3.8-7.5-8.5-7.5Z" />
      <circle cx="8" cy="11" r="1" />
      <circle cx="11" cy="7.5" r="1" />
      <circle cx="15.5" cy="9" r="1" />
    </svg>
  );
}
